import { useMemo } from 'react';
import moment from 'moment';
import { Back, Button } from 'components/quashed';
import { FieldTypeMap } from 'components/form-fields-v2';
import { Controller, useForm } from 'react-hook-form';
import { FREQUENCY } from 'constants/options';
import {
  ADDRESS_FIELD,
  FREQUENCY_QUESTION,
  PREMIUM_QUESTION,
  RENEWAL_FIELD,
} from 'constants/policy-details';
import {
  TRACKING_EVENTS,
  convertFrequency,
  prettifyNumber,
  shouldShowPremiumWarning,
  trackEventWithData,
} from 'helpers';
import styles from '../styles/renewalManual.scss';
import { FieldType } from 'types';
import { useDispatch } from 'react-redux';
import { SelectPolicyRenewalLoaded } from 'actions';
import { HOME_ROUTE } from 'constants/routes';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';

const PREMIUM_ERROR_MSG = 'Premium looks quite high, are you sure?';

const isAddressRequired = (policyType: string) => {
  return R.includes(policyType, [
    INSURANCE_TYPES_NAMES.CONTENT,
    INSURANCE_TYPES_NAMES.HOME,
    INSURANCE_TYPES_NAMES.HOME_CONTENTS,
    INSURANCE_TYPES_NAMES.LANDLORD,
    INSURANCE_TYPES_NAMES.RENTERS,
  ]);
};

const FormField = ({
  className,
  control,
  error,
  errorMsg,
  fieldKey: key,
  subText,
  type,
  validator,
  required,
  ...question
}) => {
  const FieldComponent = FieldTypeMap[type];

  return (
    <Controller
      key={key}
      control={control}
      name={key}
      rules={{
        required,
        validate: validator,
      }}
      render={({ field }) => {
        return (
          <div className={classnames(styles.fieldContainer, className)}>
            <FieldComponent
              {...field}
              {...question}
              error={error}
              className={classnames(styles.field, error && styles.error)}
              labelClassName={styles.label}
              inputClassName={styles.input}
            >
              {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
            </FieldComponent>
            {subText && <div className={styles.subText}>{subText}</div>}
          </div>
        );
      }}
    />
  );
};

const getDefaultValues = (policy) => {
  return {
    address: policy.address,
    frequency: policy.frequency,
    premium: '',
    renewalDate: moment(policy.renewalDate).add(1, 'year').utc().format(),
  };
};

const RenewalManual = ({ policy, prevButtonText, onPrevBtnClick }) => {
  const dispatch = useDispatch();
  const defaultValues = useMemo(() => getDefaultValues(policy), [policy]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({ defaultValues });

  const submit = (data) => {
    trackEventWithData(TRACKING_EVENTS.PolicyRenewalManual, {
      id: policy.id,
      ...data,
    });
    dispatch(SelectPolicyRenewalLoaded(data));
  };

  const formValues = watch();

  const currentPremium = policy.premium
    ? prettifyNumber(
        convertFrequency(policy.premium, policy.frequency, FREQUENCY.YEARLY),
      )
    : 0;

  const showPremiumWarning = shouldShowPremiumWarning(formValues);

  return (
    <form className={styles.container} onSubmit={handleSubmit(submit)}>
      <div className={styles.fields}>
        <div className={styles.col}>
          <FormField
            control={control}
            fieldKey={PREMIUM_QUESTION.key}
            {...PREMIUM_QUESTION}
            error={showPremiumWarning || errors?.[PREMIUM_QUESTION.key]}
            label="New premium"
            errorMsg={showPremiumWarning && PREMIUM_ERROR_MSG}
            subText={
              !!currentPremium && `Current premium is $${currentPremium}/yr`
            }
            required={true}
          />
          <FormField
            control={control}
            fieldKey={FREQUENCY_QUESTION.key}
            error={errors?.[FREQUENCY_QUESTION.key] || showPremiumWarning}
            {...FREQUENCY_QUESTION}
            required={true}
          />
        </div>
        <div className={styles.col}>
          <FormField
            control={control}
            fieldKey={RENEWAL_FIELD.key}
            error={errors?.[RENEWAL_FIELD.key]}
            {...RENEWAL_FIELD}
            min={new Date(defaultValues.renewalDate)}
            label="Next renewal date"
            max={undefined}
            type={FieldType.Date}
            required={true}
          />
          <FormField
            className={styles.longField}
            control={control}
            fieldKey={ADDRESS_FIELD.key}
            error={errors?.[ADDRESS_FIELD.key]}
            {...ADDRESS_FIELD}
            required={isAddressRequired(policy.policyType)}
            subText="Tip: If your address has changed, call or email your insurer to update your insured address."
          />
        </div>
      </div>
      <div className={styles.buttons}>
        <Back
          className={styles.back}
          link={!onPrevBtnClick && HOME_ROUTE}
          onClick={onPrevBtnClick}
          text={prevButtonText}
        />
        <Button className={styles.submit} text="Next" type="submit" />
      </div>
    </form>
  );
};

export default RenewalManual;
